// pages/admin/RewardManagement.tsx

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Refresh,
  NavigateNext,
  Home,
  Close,
  Search,
  CardGiftcard,
  AttachMoney,
  Stars,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListRewardsQuery,
  useAddRewardMutation,
  useUpdateRewardMutation,
  useDeleteRewardMutation,
  Reward,
  RewardFormData,
  validateAmount,
  validatePoints,
} from '../../features/admin/rewardApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
  gold: '#ffd700',
};

const RewardManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Filter states
  const [searchStr, setSearchStr] = useState('');

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [formData, setFormData] = useState<RewardFormData>({
    reward_name: '',
    amount: '',
    points: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListRewardsQuery();

  const [addReward, { isLoading: isAdding }] = useAddRewardMutation();
  const [updateReward, { isLoading: isUpdating }] = useUpdateRewardMutation();
  const [deleteReward, { isLoading: isDeleting }] = useDeleteRewardMutation();

  // Handle API response structure
  const rewards = data?.data || [];
  const totalRecords = rewards.length;

  const isEditing = Boolean(editingReward);
  const isFormLoading = isAdding || isUpdating;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      reward_name: '',
      amount: '',
      points: '',
    });
    setFormErrors({});
    setEditingReward(null);
  };

  const handleAddReward = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleEditReward = (reward: Reward) => {
    setFormData({
      reward_name: reward.reward_name || '',
      amount: reward.amount || '',
      points: reward.points || '',
      id: reward.id,
    });
    setEditingReward(reward);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.reward_name.trim()) {
      newErrors.reward_name = 'Reward name is required';
    }

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else if (!validateAmount(formData.amount)) {
      newErrors.amount = 'Invalid amount (must be a positive number)';
    }

    if (!formData.points.trim()) {
      newErrors.points = 'Points are required';
    } else if (!validatePoints(formData.points)) {
      newErrors.points = 'Invalid points (must be a positive number)';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let result;
      if (isEditing && editingReward) {
        const updateData = {
          id: formData.id || editingReward.id,
          reward_name: formData.reward_name.trim(),
          amount: formData.amount.trim(),
          points: formData.points.trim(),
        };
        
        result = await updateReward(updateData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward updated successfully!',
          severity: 'success'
        });
      } else {
        const addData = {
          reward_name: formData.reward_name.trim(),
          amount: formData.amount.trim(),
          points: formData.points.trim(),
        };
        
        result = await addReward(addData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward created successfully!',
          severity: 'success'
        });
      }
      handleFormClose();
      refetch();
    } catch (err: any) {
      console.error('Failed to save reward:', err);

      let errorMessage = `Error ${isEditing ? 'updating' : 'creating'} reward. Please try again.`;

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };
  




  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this reward?')) {
      try {
        const result = await deleteReward({ id }).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete reward:', err);

        let errorMessage = 'Error deleting reward. Please try again.';

        if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.data?.error) {
          errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Delete operation failed';
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view rewards.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No rewards found.';
        case 500:
          return 'Something went  wrong. Please try again.';
        default:
          return 'Unable to load rewards. Please try again.';
      }
    }
    return 'Network error. Please check  connection.';
  };


  // Filter rewards based on search
  const filteredRewards = rewards.filter((reward: Reward) => {
    const matchesSearch = !searchStr || 
      reward.reward_name?.toLowerCase().includes(searchStr.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="#" underline="hover">
                Users
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Rewards
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Rewards
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Manage loyalty rewards and points system
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddReward}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Add reward
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search rewards..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredRewards.length} reward{filteredRewards.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading rewards...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredRewards.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <CardGiftcard sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No rewards found' : 'No rewards yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr 
                    ? `No rewards match your search "${searchStr}"`
                    : 'Add your first reward to get started'
                  }
                </Typography>
                {!searchStr && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddReward}
                    sx={{
                      backgroundColor: shopifyColors.primary,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: shopifyColors.primaryLight,
                      },
                    }}
                  >
                    Add reward
                  </Button>
                )}
              </Card>
            )}

            {/* Rewards List */}
            {!isLoading && filteredRewards.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredRewards.map((reward: Reward, index: number) => (
                    <ListItem
                      key={reward.id}
                      sx={{
                        borderBottom: index < filteredRewards.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: shopifyColors.gold,
                            width: 48,
                            height: 48,
                          }}
                        >
                          <CardGiftcard />
                        </Avatar>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                              {reward.reward_name || 'Unnamed Reward'}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AttachMoney sx={{ fontSize: 16, color: shopifyColors.success }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Amount: ${reward.amount}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Stars sx={{ fontSize: 16, color: shopifyColors.gold }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Points: {reward.points}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, mt: 0.5, display: 'block' }}>
                              ID: {reward.id}
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleEditReward(reward)}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => handleDelete(reward.id)}
                              disabled={isDeleting}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {isEditing ? 'Edit reward' : 'Add reward'}
            </Typography>
            <IconButton 
              onClick={handleFormClose} 
              size="small" 
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Reward Name */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Reward Details
                </Typography>
                
                <TextField
                  name="reward_name"
                  label="Reward Name"
                  value={formData.reward_name}
                  onChange={handleInputChange}
                  error={!!formErrors.reward_name}
                  helperText={formErrors.reward_name || "Enter a descriptive name for the reward"}
                  fullWidth
                  required
                  placeholder="e.g., Platinum Reward, Gold Reward"
                  disabled={isFormLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Amount and Points */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Reward Value
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="amount"
                      label="Amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      error={!!formErrors.amount}
                      helperText={formErrors.amount || "Monetary value"}
                      fullWidth
                      required
                      placeholder="200"
                      disabled={isFormLoading}
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoney sx={{ fontSize: 20, color: shopifyColors.textSecondary }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="points"
                      label="Points"
                      value={formData.points}
                      onChange={handleInputChange}
                      error={!!formErrors.points}
                      helperText={formErrors.points || "Points required"}
                      fullWidth
                      required
                      placeholder="1"
                      disabled={isFormLoading}
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Stars sx={{ fontSize: 20, color: shopifyColors.gold }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, mt: 1, display: 'block' }}>
                  💡 Tip: Set the amount and points that customers earn for this reward tier
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: shopifyColors.textPrimary,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormLoading || !formData.reward_name?.trim() || !formData.amount?.trim() || !formData.points?.trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </Box>
              ) : (
                isEditing ? 'Save changes' : 'Add reward'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RewardManagement;